import React from 'react';
import c from 'classnames';
import ListCard from '../../../List/ListCard';
import StandardMenuContent, {
  StandardMenuContentProps,
} from './StandardMenuContent';
import ScrollableMenuContent from './ScrollableMenuContent';
import { setRefs } from '../../../../utils/refHelpers';
import { hasSubmenus } from '../../helpers';
import StandardMenuContext from './context';
import { StandardMenuType } from './types';
import s from './StandardMenu.scss';

export type Props = StandardMenuContentProps &
  Omit<React.HTMLAttributes<HTMLDivElement>, keyof StandardMenuContentProps> & {
    className?: string;
    label?: React.ReactNode;
    scrollable?: boolean;
    menuClassName?: string;
    labelClassName?: string;
    withCardWrapper?: boolean;
    withPaddings?: boolean; // menu paddings
    withItemHorizontalPaddings?: boolean;
    itemsFillContainerWidth?: boolean;
    withScrollableArrows?: boolean;
    onScrollToLast?: () => void;
    innerRef?: (node: HTMLElement | null) => void;
    menuType?: StandardMenuType;
  };

const StandardMenu = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      id,
      items,
      className,
      label,
      scrollable = false,
      lightText,
      hideCheckIcon,
      menuClassName,
      itemClassName,
      groupClassName,
      labelClassName,
      withHoverEffect = true,
      withCardWrapper = true,
      withPaddings = true,
      withItemHorizontalPaddings = true,
      withScrollableArrows = false,
      menuType = StandardMenuType.DROPDOWN,
      onSelect,
      innerRef,
      children,
      itemsFillContainerWidth,
      onScrollToLast,
      role,
      ...restProps
    },
    ref
  ) => {
    const hasPaddings = withPaddings && !label;
    // TODO: submenus don't work with scrollable
    const isScrollable = (scrollable || !!label) && !hasSubmenus(items);
    const contentProps: StandardMenuContentProps = {
      id,
      items,
      className: menuClassName,
      groupClassName,
      itemClassName: c(itemClassName, s['menu-item']),
      hideCheckIcon,
      withHoverEffect,
      lightText,
      onSelect,
      role,
    };

    const content = (
      <>
        {isScrollable ? (
          <ScrollableMenuContent
            label={label}
            labelClassName={c(labelClassName, s['label'])}
            withScrollableArrows={withScrollableArrows}
            onScrollToLast={onScrollToLast}
            {...contentProps}
          />
        ) : (
          <StandardMenuContent {...contentProps} />
        )}
        {children}
      </>
    );

    return (
      <StandardMenuContext.Provider
        value={{
          menuType,
          role,
        }}
      >
        {withCardWrapper ? (
          <ListCard
            className={c(s['root'], className)}
            innerRef={node => setRefs([ref, innerRef], node)}
            {...restProps}
          >
            {content}
          </ListCard>
        ) : (
          <div
            className={c(
              s['root'],
              hasPaddings && s['with-paddings'],
              // TODO: can this be ignored?
              !withItemHorizontalPaddings &&
                s['without-item-horizontal-paddings'], // negative condition, so that the default selector is weaker and allows custom overrides
              className
            )}
            ref={node => setRefs([ref, innerRef], node)}
            {...restProps}
          >
            {content}
          </div>
        )}
      </StandardMenuContext.Provider>
    );
  }
);
StandardMenu.displayName = 'StandardMenu';

export default StandardMenu;
